import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import {
    Table, TableBody, TableContainer, TableRow, TableHead, Paper, TableCell,
    CircularProgress, Alert, Accordion, AccordionSummary, AccordionDetails, Typography,
    Box
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { getCbBatchData } from '../../../../../../actions/conservation-burns/conservation-burn-actions';

export default function CbBatchData({ projectKey, measureType }) {
    const dispatch = useDispatch();
    const [hideState, setHideState] = useState({});
    const [loading, setLoading] = useState(true);
    const cbBatchData = useSelector((state) => (state.conservationBurnsReducer.batchData))

    const toggleHide = (batchId) => {
        setHideState(prev => ({
            ...prev,
            [batchId]: !prev[batchId]
        }));
    };

    useEffect(() => {
        dispatch(getCbBatchData(projectKey));
        setLoading(false);
    }, [dispatch, projectKey]);

    if (loading) return <CircularProgress />

    if (!cbBatchData?.data?.length) {
        return <Alert
            severity="info"
            sx={{ width: 'fit-content' }}>
            No Conservation Burn data for this project
        </Alert>
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant='h5'>Conservation Burn Batch Data</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {cbBatchData.data.map((batch, index) => (
                    <Accordion key={index}>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            onClick={() => toggleHide(`CB - ${batch.batchId}`)}
                        >
                            <Typography>
                                {hideState[`CB - ${batch.batchId}`] ? "Hide" : "Expand"} {batch.batchId}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <h3>Batch: {batch.batchId}</h3>

                            {/* Main Table */}
                            <TableContainer component={Paper}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Pile ID</TableCell>
                                            <TableCell>Estimated Piles</TableCell>
                                            <TableCell>Pile Sample Size</TableCell>
                                            <TableCell>Pile Diameters {measureType === 'Imperial' ? ' yrd³' : ' m³'}</TableCell>
                                            <TableCell>Pile Heights {measureType === 'Imperial' ? ' yrd³' : ' m³'}</TableCell>
                                            <TableCell>Moisture Content</TableCell>
                                            <TableCell>Bucket Count</TableCell>
                                            <TableCell>Bucket Used</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {batch.batchData.map((bd, idx) => (
                                            <TableRow key={idx}>
                                                <TableCell>{bd.pileUsed}</TableCell>
                                                <TableCell>
                                                    {bd.estimatedPiles ? `${bd.estimatedPiles}` : ""}
                                                </TableCell>
                                                <TableCell>
                                                    {bd.pileSampleSize ? `${bd.pileSampleSize}` : "0"}
                                                </TableCell>
                                                <TableCell>
                                                    {bd.pileDiameters ? `${bd.pileDiameters}${measureType === 'Imperial' ? ' yrd³' : ' m³'}` : ""}
                                                </TableCell>
                                                <TableCell>
                                                    {bd.pileHeights ? `${bd.pileHeights}${measureType === 'Imperial' ? ' yrd³' : ' m³'}` : ""}
                                                </TableCell>
                                                <TableCell>
                                                    {bd.moistureContent ? `${bd.moistureContent}%` : ""}
                                                </TableCell>
                                                <TableCell>
                                                    {bd.bucketCount ? `${bd.bucketCount}` : ""}
                                                </TableCell>
                                                <TableCell>
                                                    {bd?.bucketUsed ? `${bd.bucketUsed}` : ""}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {batch?.additionalBatchData?.length > 0 &&
                                <>
                                    <h4 style={{ marginTop: '2em' }}>Additional Batch Data:</h4>
                                    <TableContainer component={Paper}>
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Pile ID</TableCell>
                                                    <TableCell>Batch Location</TableCell>
                                                    <TableCell>Feedstock Type</TableCell>
                                                    <TableCell>Pre Ignition Checklist</TableCell>
                                                    <TableCell>Quench Team Count</TableCell>
                                                    <TableCell>Unburnt Piles</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {batch.additionalBatchData.map((bd, idx) => (
                                                    <TableRow key={idx}>
                                                        <TableCell>{bd.pileUsed}</TableCell>
                                                        <TableCell>
                                                            {bd?.location ? `${bd.location}` : ""}
                                                        </TableCell>
                                                        <TableCell>{bd?.feedstockType || ""}</TableCell>
                                                        <TableCell>{bd?.preIgnitionChecklistCompliant || ""}</TableCell>
                                                        <TableCell>{bd?.quenchTeamCount || ""}</TableCell>
                                                        <TableCell>{bd?.unburntPiles ?? ""}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </>}
                        </AccordionDetails>
                    </Accordion>
                )
                )}
            </Box>
        </Box>
    );
}